import { useMemo } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { formatNumber, formatPercent } from "../../../../utils/format";

export const buildCoverageRows = (meta) => {
  if (!meta) return [];

  const total = Number(meta.total) || 0;
  const withAkabiz = Number(meta.withAkabizTag) || 0;
  const withZalo = Number(meta.withZaloTag) || 0;
  const withBoth = Number(meta.withBothTypes) || 0;
  const withoutAnyTag = Math.max(
    0,
    Number(meta.withoutAnyTag ?? total - withAkabiz - withZalo + withBoth) || 0,
  );

  return [
    {
      label: "Không tag",
      count: withoutAnyTag,
      total,
      color: "#94a3b8",
    },
    {
      label: "Chỉ Akabiz",
      count: Math.max(0, withAkabiz - withBoth),
      total,
      color: "#3b82f6",
    },
    {
      label: "Chỉ Zalo",
      count: Math.max(0, withZalo - withBoth),
      total,
      color: "#10b981",
    },
    {
      label: "Cả hai",
      count: Math.max(0, withBoth),
      total,
      color: "#f59e0b",
    },
  ];
};

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0].payload;
  const total = row.total;
  return (
    <div className="bg-white border border-gray-200 rounded-md shadow-md px-2.5 py-2 text-xs space-y-1 min-w-[180px]">
      <div className="text-gray-700 font-medium">{row.label}</div>
      <div className="text-gray-500">
        Trong tổng {formatNumber(total)} contact trong tập lọc
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: row.color }}
          />
          <span className="text-gray-700">Số contact</span>
        </div>
        <span className="tabular-nums font-medium text-gray-900">
          {formatNumber(row.count)}
          <span className="text-gray-400 font-normal">
            {" "}
            · {formatPercent(row.count, total)}
          </span>
        </span>
      </div>
    </div>
  );
};

const TagsCoverageChart = ({ meta, loading }) => {
  const data = useMemo(() => buildCoverageRows(meta), [meta]);

  if (loading) {
    return <div className="h-[220px] bg-gray-50 rounded animate-pulse" />;
  }

  if (!meta || Number(meta.total) === 0) {
    return (
      <div className="text-sm text-gray-500 py-8 text-center">
        Chưa có contact nào có hoạt động gần nhất trong khoảng này
      </div>
    );
  }

  return (
    <div className="h-[220px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 16, left: 16, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" horizontal={false} />
          <XAxis
            type="number"
            tick={{ fontSize: 11, fill: "#6b7280" }}
            tickLine={false}
            axisLine={{ stroke: "#e5e7eb" }}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fontSize: 11, fill: "#6b7280" }}
            tickLine={false}
            axisLine={false}
            width={92}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f9fafb" }} />
          <Bar
            dataKey="count"
            radius={[4, 4, 4, 4]}
            barSize={18}
            name="Số contact"
          >
            {data.map((row) => (
              <Cell key={row.label} fill={row.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TagsCoverageChart;
